/**
 * The header's Loyalty links, pointed at the profile page once the fan has joined.
 *
 * TWO of them since 2026-08-21: the mobile menu's account row and the desktop navbar's button.
 * This module needed no change for the second one and that is not luck -- it selects by data
 * attribute with querySelectorAll and rewrites every match, so a new surface costs one more
 * element in a list that was already a list. Adding a third is the same non-event. See D-061.
 *
 * resources/views/sections/header.blade.php renders ONE href for everybody -- the join URL -- and
 * carries the profile URL alongside it in `data-profile-url`. It has to: whether a fan has joined
 * is a loyalty API read, unanswerable in PHP, and the header is on every page behind Varnish and
 * the edge, so a server-rendered decision would be handed to the wrong cohort. Same defence as
 * resources/views/partials/loyalty-join-strip.blade.php.
 *
 * This module adds NO requests, however many links it finds. It subscribes to the gate chain the
 * toast layer already runs on every page (toasts/gate.js, memoised for exactly this) exactly ONCE,
 * from initLoyaltyNavLink below -- measured at localhost:8080 with a stubbed session and a stubbed
 * enrolment read: one /api/loyalty/status for the whole page, with the toast layer and both links
 * subscribed. (That measurement used to name "one admin-ajax session call" beside it. There is no
 * such call since D-063 made the session a cookie read; gate.js records the corrected figure.)
 *
 * `armed` is the verdict it wants: signed in, live loyalty token, enrolled. Every other verdict --
 * including `unknown`, where a failed enrolment read lands -- leaves the join URL in place, which
 * is the right way to be wrong here: an already-joined fan lands on the join page, whose own hero
 * offers them their profile, whereas a not-joined fan sent to a profile page has nothing to do
 * there.
 *
 * Inert wherever the loyalty bundle is not loaded at all -- App\Loyalty\SiteWideAssets returns
 * early when THREE_HALVES_BASE_URL is unset -- and both links keep working as plain join links.
 * Neither surface's markup depends on the bundle: the anchors are server-rendered HTML gated on an
 * ACF option, so switching loyalty off costs the upgrade and nothing else.
 */

import { VERDICT, whenToastEligible } from './toasts/gate';

/**
 * The anchors the header marks for this module. Not a class, and not one selector per surface:
 * class names on those blocks deliberately avoid the substrings auth0-login.php's
 * updateAuth0MenuItems() scans for, and a data attribute cannot be caught by a `[class*=...]`
 * selector at all. It is also what lets the two surfaces keep unrelated class names --
 * `.account-link` in the mobile menu, `.nav-loyalty-btn` in the navbar -- and still be one thing
 * here. See docs/subsystems/header.md.
 */
const SELECTOR = 'a[data-loyalty-nav][data-profile-url]';

/**
 * Repoint every one of the header's Loyalty links for an enrolled fan.
 *
 * Called once from resources/scripts/loyalty.jsx. Safe to call before the gate has an answer and
 * safe on a page with no such link -- both cost one querySelectorAll and nothing else.
 *
 * @return {void}
 */
export function initLoyaltyNavLink() {
	const links = [ ...document.querySelectorAll( SELECTOR ) ].filter(
		link => link.dataset.profileUrl
	);

	// No profile page set in Brand Settings -> Global Settings, or no header on this page. An
	// editorial state, not a fault: nothing is subscribed and the gate chain is not started on this
	// account. All-or-nothing across the surfaces, because both anchors carry the same field.
	if ( ! links.length ) {
		return;
	}

	whenToastEligible( verdict => {
		if ( verdict !== VERDICT.ARMED ) {
			return;
		}

		links.forEach( link => {
			link.href = link.dataset.profileUrl;

			// The one line of debugging for "why is my Loyalty link still the join page". Stamped
			// per link, so the two surfaces can be told apart in the inspector.
			link.dataset.loyaltyNav = 'profile';
		} );
	} );
}
